import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { EnumCompanyType, MissingPhoto } from 'Interfaces/IGlobalTypeConfig';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import type {
  OrderPostCompanyViewModel,
  OrderPostProductViewModel,
  OrderPostTicketContactInfoViewModel,
} from 'pages/Orders/ViewModels/OrderPostViewModels';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import { EmployeeDrawer } from './Drawers/EmployeeDrawer';
import { boxStyle, TextStyle } from 'data/FontStyle';
import { SingleTicketEmployeeView } from './SingleTicketEmployeeView';
import { IEmployeeViewModel } from 'Interfaces/IEmployeeConfig';
import { CompaniesDrawer } from './Drawers/CompaniesDrawer';
import { SingleTicketCompanyView } from './SingleTicketCompanyView';
import { get } from 'lodash';
import { GetColorByName } from 'functions/GetColorByName';
import { FormBox } from 'components/form';

type Props = {
  index?: number;
  product: OrderPostProductViewModel;
  editable?: boolean;
  onQuantityChange?: (value: number) => void;
  onRetailPriceChange?: (value: number) => void;
  onVendorUrlChange?: (value: string) => void;
  onOwnerChange?: (owner: IEmployeeViewModel | null) => void;
  onVendorChange?: (vendor: OrderPostCompanyViewModel | null) => void;
  onRemove?: () => void;
  companyId: string;
};

type LocalState = {
  quantity: string;
  retailPrice: string;
  vendorUrl: string;
  owner: OrderPostTicketContactInfoViewModel | null;
  vendor: OrderPostCompanyViewModel | null;
};

export const SingleInvestmentProductView = ({
  product,
  editable = false,
  onQuantityChange,
  onRetailPriceChange,
  onVendorUrlChange,
  onOwnerChange,
  onVendorChange,
  onRemove,
  companyId,
}: Props) => {
  console.log('sss', product);
  const { t } = useTranslation();
  const [localCompanyId, setLocalCompanyId] = useState<string | null>(
    companyId
  );
  const [local, setLocal] = useState<LocalState>({
    quantity: product.quantity != null ? String(product.quantity) : '',
    retailPrice: product.retailPrice != null ? String(product.retailPrice) : '',
    vendorUrl: product.vendorProductUrl ?? '',
    owner: product?.owner || null,
    vendor: product?.vendor || null,
  });

  useEffect(() => {
    setLocal({
      quantity: product.quantity != null ? String(product.quantity) : '',
      retailPrice:
        product.retailPrice != null ? String(product.retailPrice) : '',
      vendorUrl: product.vendorProductUrl ?? '',
      owner: product?.owner || null,
      vendor: product?.vendor || null,
    });
  }, [
    product.quantity,
    product.retailPrice,
    product.vendorProductUrl,
    product.owner,
    product.vendor,
  ]);

  useEffect(() => {
    setLocalCompanyId(companyId);
  }, [companyId]);

  const setField = <K extends keyof LocalState>(
    field: K,
    value: LocalState[K]
  ) => setLocal((p) => ({ ...p, [field]: value }));

  const commitNumber = (field: 'quantity' | 'retailPrice') => {
    const raw = local[field].trim();
    const n = raw === '' ? 0 : Number(raw.replace(',', '.'));
    if (!Number.isFinite(n)) return;
    if (field === 'quantity') onQuantityChange?.(Math.trunc(n));
    else onRetailPriceChange?.(n);
  };
  const commitUrl = () => onVendorUrlChange?.(local.vendorUrl);

  const handleOwnerChange = (
    _e: React.MouseEvent<HTMLElement>,
    employee: IEmployeeViewModel
  ) => onOwnerChange?.(employee);

  const handleChangeCompany = (
    _e: React.MouseEvent<HTMLElement>,
    company: OrderPostCompanyViewModel | null
  ) => {
    setLocal((p) => ({ ...p, vendor: company }));
    onVendorChange?.(company);
  };

  return (
    <Box
      sx={{
        borderLeft: 15,
        borderRight: 8,
        borderRadius: 3,
        borderColor: GetColorByName(product.name),
        backgroundColor: 'primary.main',
        ':hover': {
          cursor: 'pointer',
          filter: 'brightness(105%)',
          transition: 'all 0.2s ease-in-out',
        },
        display: 'grid',
        gridTemplateColumns: editable ? '128px 1fr auto' : '88px 1fr auto', // RAD 1: bilde | tekst | tall
        columnGap: 1,
        alignItems: 'stretch',
        height: editable ? '100%' : 88,
      }}
    >
      {/* Rad 1, kolonne 1: Bilde */}
      <Box
        sx={{
          p: 0.3,
          width: editable ? 128 : 88,
          height: editable ? 128 : 88,
          borderTopLeftRadius: '3px',
          borderBottomLeftRadius: '3px',
          display: 'flex',
          gridColumn: '1',
          gridRow: '1',
        }}
      >
        <img
          src={product.url || MissingPhoto.PRODUCT_URL}
          style={{
            width: '100%',
            height: '100%',
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
            objectFit: 'scale-down',
          }}
          alt='logo'
        />
      </Box>

      {/* Rad 1, kolonne 2: Tittel/tekst */}
      <Box
        sx={{
          mb: 1,
          pb: 1,
        }}
      >
        <Grid container spacing={0} sx={{}}>
          {/* Produktnavn full bredde */}
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              sx={{
                letterSpacing: 1,
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 'bold',
                fontSize: 22,
                width: '95%',
                mb: 2,
              }}
              title={product.name}
            >
              {product.name}
            </Typography>
            {editable && (
              <IconButton
                size='small'
                color='error'
                onClick={() => {
                  if (onRemove) onRemove();
                }}
              >
                <DeleteForeverRoundedIcon />
              </IconButton>
            )}
          </Grid>

          {/* Quantity og Price på samme linje */}
          <Grid item xs={6} sx={{ pr: 1 }}>
            {editable ? (
              <TextField
                fullWidth
                size='small'
                variant='standard'
                label='Quantity'
                type='text'
                value={local.quantity}
                onChange={(e) => setField('quantity', e.target.value)}
                onBlur={() => commitNumber('quantity')}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  style: { textAlign: 'right' },
                }}
              />
            ) : (
              <></>
            )}
          </Grid>

          <Grid item xs={6} sx={{ pl: 1 }}>
            {editable ? (
              <TextField
                fullWidth
                size='small'
                type='number'
                variant='standard'
                label='Price'
                value={local.retailPrice}
                onBlur={() => commitNumber('retailPrice')}
                onChange={(e) => setField('retailPrice', e.target.value)}
                inputProps={{
                  inputMode: 'decimal',
                  style: {
                    textAlign: 'right',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    color: 'secondary.text',
                  },
                }}
              />
            ) : (
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  textAlign: 'right',
                  color: 'secondary.text',
                }}
              >
                {(product.retailPrice ?? 0).toFixed(2)}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* ---------------- RAD 2: "denne skal komme under" ---------------- */}
      <Box
        sx={{
          gridColumn: '1 / -1', // spenner over alle kolonner => LIGGER UNDER
          gridRow: '2',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mt: 1,
        }}
      >
        {editable && (
          <Box>
            {/* Product URL */}
            <TextField
              fullWidth
              size='small'
              variant='standard'
              label='Product URL'
              value={local.vendorUrl}
              onChange={(e) => setField('vendorUrl', e.target.value)}
              onBlur={commitUrl}
            />

            {/* Owner */}
            <Grid item xs={12} sx={{ p: 0, mb: 2 }}>
              <FormBox
                Icon={PersonSearchRoundedIcon}
                title={t('ticketProductDrawer.selectOwner')}
              />
            </Grid>
            {product.owner && product.owner.id ? (
              <Box sx={{ position: 'relative', mb: 0.5 }}>
                <SingleTicketEmployeeView
                  employee={product.owner}
                  priority={0}
                />
                <Box sx={{ position: 'absolute', top: 0, right: 3 }}>
                  <EmployeeDrawer
                    buttonIcon={<ChangeCircleIcon sx={{ fontSize: 40 }} />}
                    buttonContext={t('button.addResponsible')}
                    handleChange={handleOwnerChange}
                    companyId={localCompanyId ?? ''}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                sx={
                  [boxStyle.base, { height: 60, position: 'relative' }] as any
                }
              >
                <Button
                  sx={{
                    mx: 'auto',
                    width: '100%',
                    height: '100%',
                    borderRadius: 3,
                  }}
                  variant='contained'
                  onClick={() => {
                    /* handleAssignMe(); */
                  }}
                >
                  <Typography sx={[TextStyle.text, {}]}>
                    {t('ticketProductDrawer.selectOwner')}
                  </Typography>
                </Button>
                <Box sx={{ position: 'absolute', top: 0, right: 3 }}>
                  <EmployeeDrawer
                    buttonIcon={<ChangeCircleIcon sx={{ fontSize: 26 }} />}
                    buttonContext={t('button.addResponsible')}
                    handleChange={handleOwnerChange}
                    companyId={companyId}
                  />
                </Box>
              </Box>
            )}

            {/* Vendor */}
            <Grid item xs={12} sx={{ p: 0, mb: 2 }}>
              <FormBox
                Icon={PersonSearchRoundedIcon}
                title={t('ticketProductDrawer.selectVendor')}
              />
            </Grid>
            <Box sx={{ position: 'relative', mb: 0 }}>
              {product.vendor && product.vendor.id ? (
                <Stack direction='column' sx={{ width: '100%' }}>
                  <SingleTicketCompanyView company={product.vendor} />
                  <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                    <CompaniesDrawer
                      buttonContext={t('ticketProductDrawer.selectVendor')}
                      buttonIcon={<ChangeCircleIcon sx={{ fontSize: 26 }} />}
                      handleChange={handleChangeCompany}
                      isHideCompanyView
                      isHideCompanyNavigation
                      companyType={EnumCompanyType.VENDOR}
                    />
                  </Box>
                </Stack>
              ) : (
                <Box
                  sx={
                    [
                      boxStyle.base,
                      { height: 60, width: '100%', position: 'relative' },
                    ] as any
                  }
                >
                  <Button
                    sx={{
                      mx: 'auto',
                      width: '100%',
                      height: '100%',
                      borderRadius: 3,
                    }}
                    variant='contained'
                    onClick={() => {
                      /* handleAssignMe(); */
                    }}
                  >
                    <Typography sx={[TextStyle.text, {}]}>
                      {t('Ticket.TicketCommunications.assignCustomerEmployee')}
                    </Typography>
                  </Button>
                  <Box sx={{ position: 'absolute', top: 0, right: 3 }}>
                    <CompaniesDrawer
                      buttonContext={t('Ticket.TicketDetails.selectCompany')}
                      buttonIcon={<ChangeCircleIcon sx={{ fontSize: 26 }} />}
                      handleChange={handleChangeCompany}
                      isHideCompanyView
                      isHideCompanyNavigation
                      companyType={EnumCompanyType.VENDOR}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
